export type LeadSource = "wordpress" | "meta" | "hubspot" | "wayfinder" | "other";
export type LeadStatus = "MQL" | "SQL";
export type DeliveryState = "not_requested" | "delivered" | "pending" | "failed";

export interface DeliveryAttempt {
  channel: "Webhook" | "Sales email";
  at: string;
  result: "success" | "failed" | "retry";
  detail: string;
  deliveryKind: "initial" | "reminder_1" | "reminder_2";
}

export type OutcomeType =
  "call_booked" | "consultation" | "proposal_sent" | "won" | "lost" | "custom";

export interface Outcome {
  id: string;
  type: OutcomeType;
  value?: number;
  note?: string;
  at: string;
  by: string;
}

export interface Lead {
  id: string;
  version: number;
  isNew: boolean;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
  practice: string;
  practicePostcode: string;
  practiceAddress: string;
  practiceType: string;
  enquiryNotes: string;
  source: LeadSource;
  status: LeadStatus;
  delivery: DeliveryState;
  deliveryChannels: Array<{
    channel: "email" | "webhook";
    status: "pending" | "delivered" | "failed";
  }>;
  deliveryLog: DeliveryAttempt[];
  notes: string;
  capturedAt: string;
  finalOutcome?: "won" | "lost" | null;
  outcomes: Outcome[];
}

export const sourceMeta: Record<
  LeadSource,
  { label: string; icon: string; brand: boolean; color: string }
> = {
  wordpress: { label: "WordPress form", icon: "fa-wordpress", brand: true, color: "#21759B" },
  meta: { label: "Meta Lead Form", icon: "fa-meta", brand: true, color: "#0866FF" },
  hubspot: { label: "HubSpot nurture", icon: "fa-hubspot", brand: true, color: "#FF7A59" },
  wayfinder: { label: "Wayfinder sync", icon: "fa-compass", brand: false, color: "#033651" },
  other: { label: "Manual entry", icon: "fa-user-plus", brand: false, color: "#52606D" },
};

export const outcomeCatalog: Record<
  OutcomeType,
  {
    label: string;
    icon: string;
    tone: "green" | "amber" | "blue" | "red" | "neutral";
    hasValue?: boolean;
  }
> = {
  call_booked: { label: "Call booked", icon: "fa-phone-volume", tone: "blue" },
  consultation: { label: "Consultation held", icon: "fa-handshake", tone: "blue" },
  proposal_sent: {
    label: "Proposal sent",
    icon: "fa-file-signature",
    tone: "amber",
    hasValue: true,
  },
  won: { label: "Won", icon: "fa-trophy", tone: "green", hasValue: true },
  lost: { label: "Lost", icon: "fa-circle-xmark", tone: "red" },
  custom: { label: "Note", icon: "fa-flag", tone: "neutral" },
};

export const deliveryStateMeta: Record<
  DeliveryState,
  { label: string; tone: "green" | "amber" | "red" | "neutral" }
> = {
  not_requested: { label: "Destination not configured", tone: "neutral" },
  delivered: { label: "Delivered", tone: "green" },
  pending: { label: "Pending", tone: "amber" },
  failed: { label: "Failed", tone: "red" },
};
